import React, { useRef, useState } from "react";
import { Box, Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { SubTitleText, TitleText } from "pages/home/styles";
import { COLORS } from "constants/colors";
import { gaClickCallback } from "helpers/common";
import { cl } from "./WelcomeScreen.styles";

const videoSteps = [
  {
    src: "/lovable-uploads/upload.mov",
    title: "Upload Your Data",
    text: "Import from CSV, Excel or SPSS in seconds.",
  },
  {
    src: "/lovable-uploads/validation.mov",
    title: "Validate Data Automatically",
    text: "Automatically examine your data.\nMissing values, outliers, and structure issues.",
  },
  {
    src: "/lovable-uploads/ask.mov",
    title: "Ask Your Question",
    text: "Pick from suggested questions,\nor type your own analysis prompt.",
  },
  {
    src: "/lovable-uploads/tables.mov",
    title: "View Your Results",
    text: "Graphs, tables, and conclusions.\nAll generated in seconds.",
  },
];

export const WelcomeScreen = ({ setOpenRegister }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleVideoEnd = () => {
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % videoSteps.length);
    }, 500);
  };

  const { src, title, text } = videoSteps[currentIndex];

  return (
    <Stack sx={cl.root} ref={ref}>
      {/* LEFT SIDE */}
      <Box
        flex="1"
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          height: { xs: "calc(100svh - 188px)", md: "100%" },
          minHeight: { xs: "calc(100svh - 188px)", md: "100%" },
          maxHeight: { xs: "calc(100svh - 188px)", md: "100%" },
          justifyContent: "center",
          alignItems: "center",
          boxSizing: "border-box",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, ease: "easeInOut" }}
        >
          <TitleText
            sx={{
              mb: 2,
              textAlign: { xs: "center", md: "left" },
            }}
          >
            Publication-
            <br />
            Ready Statistics
            <Box
              component="img"
              src="/lovable-uploads/dot.png"
              alt="dot"
              sx={{
                display: "inline-block",
                width: { xs: 16, md: 24 },
                height: { xs: 16, md: 24 },
                mx: { xs: 0.5, md: 1 },
                filter: `drop-shadow(0 0 2px ${COLORS.yellowDark})`,
              }}
            />
            <br />
            On Demand{" "}
            <Box
              component="img"
              src="/lovable-uploads/dot.png"
              alt="dot"
              sx={{
                display: "inline-block",
                width: { xs: 16, md: 24 },
                height: { xs: 16, md: 24 },
                mx: { xs: 0.5, md: 1 },
                filter: `drop-shadow(0 0 2px ${COLORS.yellowDark})`,
              }}
            />
          </TitleText>

          <SubTitleText
            sx={{
              textAlign: { xs: "center", md: "left" },
              mb: { xs: 3, md: 0 },
            }}
          >
            No Coding. No Hallucinations. <br />
            Upload your data, Request analysis, and get exact, reliable, publication-ready results,
            immediately.
          </SubTitleText>

          <Box
            sx={{
              display: "flex",
              gap: { xs: 2, md: 3 },
              flexDirection: { xs: "column", sm: "row" },
              flexWrap: "wrap",
              justifyContent: { xs: "center", md: "flex-start" },
              mt: { xs: 4, md: 6 },
              alignItems: { xs: "center", sm: "flex-start" },
              width: "100%",
            }}
          >
            <Button
              variant="contained"
              sx={{
                backgroundColor: COLORS.purpleLight,
                px: { xs: 3, md: 5 },
                py: { xs: 1.25, md: 1.5 },
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "1rem", md: "16px" },
                maxWidth: { xs: "320px", sm: "none" },
                width: { xs: "100%", sm: "209px" },
                height: { xs: "56px", md: "54px" },
                "&:hover": {
                  backgroundColor: COLORS.purpleDark,
                },
              }}
              onClick={() =>
                gaClickCallback({
                  label: "Get Started For Free",
                  callback: () => setOpenRegister(true),
                })
              }
            >
              Try Now for Free
            </Button>
            <Button
              onClick={() => navigate("/demo")}
              variant="outlined"
              sx={{
                backgroundColor: COLORS.white,
                border: `2px solid ${COLORS.purpleLight}`,
                color: COLORS.purpleLight,
                px: { xs: 3, md: 5 },
                py: { xs: 1.25, md: 1.5 },
                borderRadius: "12px",
                fontWeight: 600,
                fontSize: { xs: "1rem", md: "16px" },
                textTransform: "none",
                width: { xs: "100%", sm: "209px" },
                height: { xs: "56px", md: "54px" },
                maxWidth: { xs: "320px", sm: "none" },
                "&:hover": {
                  backgroundColor: "#f4f4f4",
                },
              }}
            >
              Watch Demo
            </Button>
          </Box>
        </motion.div>
      </Box>

      {/* RIGHT SIDE */}
      <Box
        flex="1"
        sx={{
          textAlign: "center",
          width: "100%",
          mt: { xs: 2, md: 0 },
        }}
      >
        <Box
          sx={{
            position: "relative",
            px: { xs: 1, md: 0 },
          }}
        >
          {/* Circle */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            style={{
              position: "absolute",
              top: isMobile ? -40 : -60,
              left: "50%",
              transform: "translateX(-50%)",
              width: isMobile ? 36 : 44,
              height: isMobile ? 36 : 44,
              backgroundColor: COLORS.purpleDark,
              color: "#fff",
              fontWeight: "bold",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: isMobile ? "1rem" : "1.25rem",
              boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              zIndex: 2,
            }}
          >
            {currentIndex + 1}
          </motion.div>

          {/* Title */}
          <motion.div
            key={`title-${currentIndex}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          >
            <Typography
              variant="h5"
              fontWeight="bold"
              sx={{
                mb: 0,
                mt: 0,
                fontSize: { xs: "1.25rem", md: "1.5rem" },
              }}
            >
              {title}
            </Typography>
          </motion.div>

          {/* Video */}
          <motion.video
            key={src}
            src={src}
            onEnded={handleVideoEnd}
            muted
            autoPlay
            playsInline
            style={{
              width: "100%",
              maxWidth: isMobile ? "100%" : 800,
              borderRadius: isMobile ? 8 : 12,
              marginTop: isMobile ? "1rem" : "1.5rem",
            }}
          />

          <Box sx={{ height: "75px" }}>
            {/* Description */}
            <motion.div
              key={`desc-${currentIndex}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, ease: "easeInOut" }}
              style={{ marginTop: "1.5rem" }}
            >
              <Typography
                variant="h5"
                color={COLORS.gray800}
                sx={{
                  maxWidth: { xs: "100%", md: 500 },
                  mx: "auto",
                  whiteSpace: "pre-line",
                  lineHeight: 1.5,
                  fontSize: { xs: "1rem", md: "1.25rem" },
                  px: { xs: 1, md: 0 },
                }}
              >
                {text}
              </Typography>
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Stack>
  );
};
